/**
 * Navigation Invariants
 *
 * Enforces:
 * - Permissions (who can navigate where)
 * - State normalization (history limits)
 */

import { Invariants } from '@commandDrivenStateEngine/core/invariants';
import { NavigationState, selectors } from './state';
import { NavigationCommand } from './commands';

/**
 * Normalize state for consistency.
 */
const normalize = ( state: NavigationState ): NavigationState => {
  let normalized = { ...state };

  // Limit history to last 100 entries
  if ( normalized.history.length > 100 ) {
    normalized = {
      ...normalized,
      history: normalized.history.slice( -100 )
    };
  }

  return normalized;
};

/**
 * Permission control - who can execute which commands.
 *
 * Source hierarchy:
 * - 'user': Can request navigation to any route
 * - 'ai': Can only navigate to routes with 'ai' in allowedSources
 * - 'api': Can update location and record history
 * - 'url': Can update location
 */
const canApply = ({
  command,
  meta
}: {
  state: NavigationState;
  command: NavigationCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: can update location and record history
  if ( source === 'api' ) {
    return (
      type === 'navigation/location/update' ||
      type === 'navigation/history/record' ||
      type === 'navigation/request/clear'
    );
  }

  // URL source: can only update location
  if ( source === 'url' ) {
    return type === 'navigation/location/update';
  }

  // AI source: check if navigation target is AI-accessible
  if ( source === 'ai' && type === 'navigation/request/navigate' ) {
    const targetPath = command.payload.path;
    return selectors.isPathAccessible( targetPath, 'ai' );
  }

  // User source: can navigate anywhere, but not API commands
  if ( source === 'user' ) {
    return (
      type === 'navigation/request/navigate' ||
      type === 'navigation/request/clear'
    );
  }

  // Unknown sources: deny
  return false;
};

export const invariants: Invariants<NavigationState, NavigationCommand> = {
  normalize,
  canApply
};
